import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  getCompany,
  updateCompany,
  transitionCompanyStatus,
  COMPANY_STATUSES,
  type Company,
  type UpdateCompanyInput,
} from '../../api/companyApi.js';
import { CompanyBrandingProfileTab } from './CompanyBrandingProfileTab.js';
import { PAN_PATTERN as PAN_RE } from '../../../../shared/contracts/backend-enums.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\-\s]{7,20}$/;

const EDIT_FIELDS: readonly { key: keyof UpdateCompanyInput; label: string; type?: 'text' | 'email' | 'textarea' }[] = [
  { key: 'companyName', label: 'Name' },
  { key: 'legalName', label: 'Legal name' },
  { key: 'panNumber', label: 'PAN number' },
  { key: 'country', label: 'Country' },
  { key: 'state', label: 'State' },
  { key: 'district', label: 'District' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website', label: 'Website' },
];

export function CompanyEditPage(): ReactNode {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const nav = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [version, setVersion] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  // §10 status transition
  const [toStatus, setToStatus] = useState('');
  const [reason, setReason] = useState('');
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  // Details stays the default so the page opens on what it has always shown.
  const [tab, setTab] = useState<'details' | 'profile'>('details');

  useEffect(() => {
    let alive = true;
    if (!id) return;
    getCompany(id, token)
      .then((c) => {
        if (!alive) return;
        setCompany(c);
        setVersion(c.version);
        setToStatus(c.status);
        const next: Record<string, string> = {};
        for (const f of EDIT_FIELDS) {
          const v = (c as unknown as Record<string, unknown>)[f.key as string];
          next[f.key as string] = v == null ? '' : String(v);
        }
        setForm(next);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [id, token]);

  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaveOk(false);
  };

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.companyName || form.companyName.trim().length < 2)
      e.companyName = t('companies.err.nameRequired');
    if (form.panNumber && !PAN_RE.test(form.panNumber))
      e.panNumber = t('companies.err.pan');
    if (form.email && !EMAIL_RE.test(form.email))
      e.email = t('companies.err.email');
    if (form.phone && !PHONE_RE.test(form.phone))
      e.phone = t('companies.err.phone');
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setSaveOk(false);
    if (!id || !validate()) return;
    const draft: Record<string, unknown> = { version };
    for (const f of EDIT_FIELDS) {
      draft[f.key as string] = form[f.key as string] ?? '';
    }
    const body: UpdateCompanyInput = draft as unknown as UpdateCompanyInput;
    setSaving(true);
    try {
      const saved = await updateCompany(id, body, token);
      setVersion(saved.version);
      setCompany(saved);
      setSaveOk(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onTransition = async (ev: FormEvent) => {
    ev.preventDefault();
    setStatusMsg(null);
    setError(null);
    if (!id) return;
    if (reason.trim().length < 5) {
      setStatusMsg(t('companies.err.reason'));
      return;
    }
    try {
      const saved = await transitionCompanyStatus(
        id,
        { toStatus: toStatus as Company['status'], reason, version },
        token,
      );
      setCompany(saved);
      setVersion(saved.version);
      setReason('');
      setStatusMsg(t('companies.statusChanged', { status: saved.status }));
    } catch (err) {
      setStatusMsg((err as Error).message);
    }
  };

  if (error && !company) return <p role="alert">{error}</p>;
  if (!company) return <p>{t('companies.loading')}</p>;

  return (
    <main aria-label="company-edit">
      <h1>{t('companies.editTitle', { code: company.companyCode })}</h1>
      <p data-testid="current-status">{t('companies.statusLabel')} {company.status}</p>
      {error ? <p role="alert">{error}</p> : null}
      {saveOk ? <p role="status">{t('companies.saved')}</p> : null}

      <div role="tablist" aria-label="company-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'details'}
          onClick={() => setTab('details')}
          data-testid="tab-details"
        >
          {t('companies.tab.details')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'profile'}
          onClick={() => setTab('profile')}
          data-testid="tab-profile"
        >
          {t('companies.tab.profile')}
        </button>
      </div>

      {tab === 'profile' && id ? <CompanyBrandingProfileTab companyId={id} /> : null}

      {tab === 'details' ? (
        <>
      <form onSubmit={onSubmit} aria-label="company-edit-form" noValidate>
        {EDIT_FIELDS.map((f) => {
          const idf = `cef-${String(f.key)}`;
          const val = form[f.key as string] ?? '';
          const err = errors[f.key as string];
          return (
            <div key={String(f.key)} className="company-field">
              <label htmlFor={idf}>{t(`companies.field.${String(f.key)}`)}</label>
              {f.type === 'textarea' ? (
                <textarea
                  id={idf}
                  value={val}
                  onChange={(e) => set(f.key as string, e.target.value)}
                  data-testid={`field-${String(f.key)}`}
                />
              ) : (
                <input
                  id={idf}
                  type={f.type === 'email' ? 'email' : 'text'}
                  value={val}
                  onChange={(e) => set(f.key as string, e.target.value)}
                  data-testid={`field-${String(f.key)}`}
                />
              )}
              {err ? (
                <span className="field-error" data-testid={`err-${String(f.key)}`}>
                  {err}
                </span>
              ) : null}
            </div>
          );
        })}
        <button type="submit" disabled={saving} data-testid="submit-edit">
          {saving ? t('companies.saving') : t('companies.save')}
        </button>
      </form>

      <form onSubmit={onTransition} aria-label="company-status-form" noValidate>
        <h2>{t('companies.changeStatus')}</h2>
        <label htmlFor="cef-status">{t('companies.newStatus')}</label>
        <select
          id="cef-status"
          value={toStatus}
          onChange={(e) => setToStatus(e.target.value)}
          data-testid="field-status"
        >
          {COMPANY_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label htmlFor="cef-reason">{t('companies.reason')}</label>
        <input
          id="cef-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="field-reason"
        />
        <button type="submit" data-testid="submit-status">
          {t('companies.applyStatus')}
        </button>
        {statusMsg ? <p role="status">{statusMsg}</p> : null}
      </form>
        </>
      ) : null}

      <button type="button" onClick={() => nav('/admin/companies')} data-testid="back">
        {t('companies.backToList')}
      </button>
    </main>
  );
}
